// The lesson. Eleven chapters, each one motivated by a problem the previous
// chapter created — because that is what an LSM tree actually is: a chain of
// consequences, not a list of parts.
//
// Every chapter's demo (except chapter 0's labelled illustration) runs the
// real engine at tiny scale.

import { DemoSpec } from "./minidemo";

export interface Chapter {
  id: string;
  /** Short label for the progress rail. */
  nav: string;
  /** The question this chapter answers. */
  kicker: string;
  title: string;
  /** Paragraphs before the demo. */
  body: string[];
  /** Prompt shown directly above the demo, telling the learner what to try. */
  tryIt?: string;
  demo?: DemoSpec;
  /** Paragraphs after the demo — the resolution. */
  after?: string[];
  /** The problem this chapter leaves behind, which the next one solves. */
  cliffhanger?: string;
  /** Optional aside: jargon, or a real-system note. */
  aside?: { title: string; text: string };
}

const V = (n: number) => "v" + n;

export const CHAPTERS: Chapter[] = [
  // ---------------------------------------------------------------- 0
  {
    id: "problem",
    nav: "The problem",
    kicker: "Start here",
    title: "Why databases dread random writes",
    body: [
      "Before any of this makes sense, you need the problem it solves. Suppose you are storing users, and you keep them sorted by ID so you can look one up quickly. Now someone updates user 40,000.",
      "The classic answer — the one used by MySQL's InnoDB, PostgreSQL, and almost every database before about 2010 — is a B-tree: find the page holding that user and rewrite it where it sits. This is called updating in place, and for reads it is excellent. For writes it has one problem: each update lands in a different, unpredictable spot.",
      "On a spinning disk, moving the head to an arbitrary location costs milliseconds. Do that once per update and a few thousand writes per second is your ceiling. SSDs removed the moving parts, but not the underlying issue — they must erase whole blocks before rewriting them, so scattered small writes still wear the drive and still cost far more than appending.",
      "Appending to the end of a file, by contrast, is the single fastest thing storage hardware does. So here is the question that produced the LSM tree:",
    ],
    tryIt: "Write some records both ways and watch the cost diverge.",
    after: [
      "If you could somehow turn every random write into an append, writes would get dramatically cheaper. That is the entire idea. Everything else in this lesson is the price of making it work.",
    ],
    cliffhanger:
      "But a database can't just append blindly — reads need to find data, and appending leaves it scattered. So where does the data go first?",
    aside: {
      title: "Who actually does this",
      text: "LSM trees run underneath RocksDB (Meta), LevelDB (Google), Cassandra and ScyllaDB, HBase, InfluxDB, and — through RocksDB — CockroachDB, TiKV, and Kafka Streams' state stores. If you have used a large-scale key-value or time-series store in the last decade, you have used one.",
    },
  },

  // ---------------------------------------------------------------- 1
  {
    id: "memtable",
    nav: "Buffer in memory",
    kicker: "Consequence 1",
    title: "Don't touch the disk yet — buffer in memory",
    body: [
      "The first move is the obvious one: don't write to disk on every put. Keep recent writes in memory and deal with the disk later, in bulk.",
      "This in-memory buffer is called the memtable. It has one requirement beyond just holding data: it must stay sorted by key. You will see why in two chapters — sorting it here, once, in memory, is what makes everything downstream cheap.",
      "This engine uses a skiplist for it, the same structure LevelDB and RocksDB use. A skiplist is a linked list with express lanes: each entry gets a random 'tower' height, tall towers skip far ahead, short ones refine the position. Searching takes about log(n) steps and, unlike a balanced tree, nothing ever has to be rebalanced.",
    ],
    tryIt:
      "Write these in any order you like and watch where they land. Keep an eye on the size counter underneath — the buffer has a limit, and we'll hit it two chapters from now.",
    demo: {
      // Roomy enough that all four keys fit: this chapter teaches ordering,
      // and a surprise flush here would make the keys vanish before the
      // learner has any idea what a flush is.
      config: { memtableBytes: 160 },
      panels: ["memtable"],
      expandTables: false,
      buttons: [
        {
          label: "put mango",
          run: async (db) => void (await db.put("mango", V(1))),
        },
        {
          label: "put apple",
          run: async (db) => void (await db.put("apple", V(2))),
        },
        {
          label: "put pear",
          run: async (db) => void (await db.put("pear", V(3))),
        },
        {
          label: "put fig",
          run: async (db) => void (await db.put("fig", V(4))),
        },
      ],
      note: "Real skiplist, real sorting. The counter shows the buffer filling toward its (deliberately tiny) limit.",
    },
    after: [
      "Notice the keys sit in alphabetical order regardless of the order you clicked them in. That ordering is maintained on every single insert, so the buffer is never 'unsorted and waiting to be sorted' — it is always ready to be written straight out.",
      "Writes are now essentially free: one insert into memory, no disk at all. Which raises an uncomfortable question.",
    ],
    cliffhanger:
      "Memory is volatile. If the process crashes right now, every one of those writes is gone — and we already told the user they succeeded. That's data loss.",
  },

  // ---------------------------------------------------------------- 2
  {
    id: "wal",
    nav: "Survive a crash",
    kicker: "Consequence 2",
    title: "The write-ahead log",
    body: [
      "We have a problem of our own making: writes are fast because they only touch memory, and memory dies with the process.",
      "The fix is to write the data to disk after all — but as an append, never a seek. Before a write is acknowledged, its record is appended to a log file. That is all the write-ahead log is: a running, append-only record of everything that happened, in order.",
      "Two rules make it work, and both are easy to get wrong. First, the log record must be durable before you tell the user the write succeeded — otherwise you can acknowledge something a crash then loses. Second, a log record can only be discarded once its data is safely stored elsewhere.",
      "Recovery becomes almost boringly simple: on startup, replay the log back into a fresh memtable. Whatever was acknowledged is either already in a file or still in the log.",
    ],
    tryIt:
      "Write a few keys, then crash the database. The memtable is wiped — watch what the log restores.",
    demo: {
      // Headroom so repeated clicking can't trip a flush here — flushing is
      // the next chapter's lesson, and an unexplained one would just look
      // like the data vanishing.
      config: { memtableBytes: 400 },
      panels: ["wal", "memtable"],
      buttons: [
        {
          label: "put 3 keys",
          run: async (db, d) => {
            for (const [k, v] of [
              ["apple", "red"],
              ["fig", "purple"],
              ["mango", "yellow"],
            ]) {
              await db.put(k, v);
              d.say(`put ${k} → logged, then buffered`);
              d.render();
              await d.pause(300);
            }
            d.say("3 writes acknowledged — all in memory, all in the log");
          },
        },
        {
          label: "💥 crash",
          title: "Discard all memory, exactly like a process kill",
          run: async (_db, d) => {
            d.say("crash — memory is gone…");
            await d.pause(600);
            await d.crashRecover();
            d.say("reopened: the log replayed every acknowledged write back into the memtable");
          },
        },
      ],
      note: "The crash button genuinely throws the memtable away and reopens the engine against the same storage. Nothing is stashed aside.",
    },
    after: [
      "Nothing was lost, and still no random writes happened — the log is pure append. The cost is that every write now touches disk once, sequentially, which is the cheapest possible way to touch it.",
    ],
    cliffhanger:
      "But the memtable can't grow forever. RAM is finite, and a log that is never truncated eventually becomes a very slow way to reload your entire database.",
  },

  // ---------------------------------------------------------------- 3
  {
    id: "flush",
    nav: "Flush to a file",
    kicker: "Consequence 3",
    title: "When the buffer fills, write one sorted file",
    body: [
      "When the memtable hits its size limit, the engine freezes it and writes the whole thing to disk as a single file, then starts a fresh memtable so writes never block.",
      "Because the memtable was already sorted, this is one sequential pass — no sorting happens at flush time. That is the payoff for keeping the buffer ordered back in chapter 1.",
      "The resulting file is called an SSTable (sorted string table, a name inherited from Google's Bigtable paper). It has one defining property: it is immutable. Once written, it is never modified again. Not for updates, not for deletes, not ever.",
      "Immutability sounds restrictive, but it buys a lot: no locking, caching that never needs invalidating, and safe concurrent reads. Almost every remaining chapter is about living with it.",
    ],
    tryIt:
      "Fill the buffer past its limit and watch a file appear. Then look at what's inside it — those are decoded from real serialized bytes.",
    demo: {
      panels: ["wal", "memtable", "levels"],
      expandTables: true,
      buttons: [
        {
          label: "write until it flushes",
          run: async (db, d) => {
            const words: [string, string][] = [
              ["apple", "red"],
              ["fig", "purple"],
              ["kiwi", "green"],
              ["mango", "gold"],
              ["pear", "green"],
            ];
            for (const [k, v] of words) {
              await db.put(k, v);
              d.say(`put ${k}`);
              d.render();
              await d.pause(320);
            }
            await db.quiesce();
            d.say("buffer hit its limit → frozen → written out as one sorted file");
          },
        },
      ],
      note: "The keys shown inside the file are decoded from its ArrayBuffer, not from a copy kept in memory.",
    },
    after: [
      "Notice the log shrank. Every write covered by that new file has had its log record discarded — those records have done their job. Only writes that arrived after the flush are still in the log, because they are still only in memory. That is rule two from the last chapter, in action.",
      "Writes are now: append to a log, insert into memory, and occasionally dump one sorted file. Still no random writes anywhere.",
    ],
    cliffhanger:
      "Do this repeatedly and you get files. Lots of files. And a key you're looking for could be in any of them.",
  },

  // ---------------------------------------------------------------- 4
  {
    id: "reads",
    nav: "Reading it back",
    kicker: "Consequence 4",
    title: "Reads search newest to oldest",
    body: [
      "Here is the cost of never updating in place: when you change a key, the old value is still sitting in an older file. The same key can exist in five places at once, with five different values.",
      "So which one is correct? The engine does not track that. It relies on something simpler and much harder to get wrong: newer data is always in a newer place. Search from newest to oldest, and the first copy you find is by definition the current one.",
      "The order is: the memtable first (newest writes), then each level of files from shallowest to deepest. Stop at the first hit.",
      "There is one more shortcut. Every file records the smallest and largest key it contains, so if your key falls outside that range, the file cannot possibly hold it and is skipped without reading a byte.",
    ],
    tryIt:
      "Look up a key that exists, then one that doesn't. Read the trace — it's the engine reporting each step as it takes it.",
    demo: {
      panels: ["levels", "trace"],
      expandTables: true,
      preload: [
        ["apple", "red"],
        ["fig", "purple"],
        ["kiwi", "green"],
        ["mango", "gold"],
        ["pear", "green"],
        ["plum", "blue"],
      ],
      buttons: [
        {
          label: "get apple",
          run: async (db, d) => {
            const v = await db.get("apple");
            d.say(`get apple → ${v === null ? "not found" : `"${v}"`}`);
          },
        },
        {
          label: "get zebra (missing)",
          run: async (db, d) => {
            const v = await db.get("zebra");
            d.say(`get zebra → ${v === null ? "not found" : `"${v}"`}`);
          },
        },
      ],
      note: "Each trace line is emitted by the engine at the moment it performs that step.",
    },
    after: [
      "A hit stops early. A miss is the expensive case — it has to rule out every file that could plausibly contain the key.",
    ],
    cliffhanger:
      "With a handful of files that's fine. With two hundred, every miss becomes two hundred file checks. Reads are now the bottleneck.",
  },

  // ---------------------------------------------------------------- 5
  {
    id: "bloom",
    nav: "Skip files fast",
    kicker: "Consequence 5",
    title: "Bloom filters: proving a key isn't there",
    body: [
      "Most files do not contain the key you are asking for. If we could cheaply prove absence, we could skip almost all of them without reading their contents.",
      "A bloom filter does exactly that. It is a small array of bits. When a key is added, a few hash functions each set one bit. To test a key, check those same bits: if any of them is zero, the key was definitely never added.",
      "The trick is that the error only goes one way. It can say 'maybe' about a key that isn't there (a false positive, because other keys happened to set the same bits), but it can never say 'no' about a key that is. So a 'no' is safe to trust, and that is the answer we need.",
      "At 10 bits per key — the default here — false positives run about 1%. Ten bits. Not ten bytes.",
      "When the filter does say 'maybe', the file still isn't read end to end. Each SSTable carries a sparse index holding every 16th key with its exact byte offset. The engine binary-searches that, jumps straight to the right region, and decodes at most 16 entries.",
    ],
    tryIt:
      "Look up missing keys and watch how many files get rejected outright. Compare the trace with chapter 4's.",
    demo: {
      panels: ["levels", "trace"],
      preload: [
        ["apple", "red"],
        ["fig", "purple"],
        ["kiwi", "green"],
        ["mango", "gold"],
        ["pear", "green"],
        ["plum", "blue"],
        ["quince", "yellow"],
        ["rhubarb", "pink"],
      ],
      buttons: [
        {
          label: "get lychee (missing)",
          run: async (db, d) => {
            const before = db.metrics.bloomSkips;
            await db.get("lychee");
            d.say(
              `not found — bloom filters rejected ${db.metrics.bloomSkips - before} file(s) without reading data`,
            );
          },
        },
        {
          label: "get mango (present)",
          run: async (db, d) => {
            const v = await db.get("mango");
            d.say(`found "${v}" — note the sparse-index seek to a byte offset`);
          },
        },
      ],
      note: "'bloom-reject' means the file's data section was never touched. 'bloom said maybe, scan found nothing' is a real false positive.",
    },
    after: [
      "This is why LSM trees can tolerate having many files: checking a file that doesn't have your key costs a few bit tests, not a disk read.",
    ],
    cliffhanger:
      "Reads are fast again. But we still haven't dealt with the old copies piling up — and we haven't even tried to delete anything yet.",
  },

  // ---------------------------------------------------------------- 6
  {
    id: "deletes",
    nav: "Updates & deletes",
    kicker: "Consequence 6",
    title: "You can't erase from a file you can't modify",
    body: [
      "Updating a key does not overwrite anything — it just writes a newer version, which shadows the old one during a read. So every update leaves litter behind.",
      "Deletion is stranger. The key might live in a file that is immutable by design. You cannot reach in and remove it. So instead the engine writes a new record meaning 'this key is deleted' — a tombstone.",
      "A read that hits a tombstone first reports 'not found'. Older values still physically exist below it, but the search never reaches them, so they are invisible. Deleting data makes the database bigger, not smaller — at least for a while.",
    ],
    tryIt:
      "Write the same key three times, then delete it. Watch the versions accumulate rather than replace each other.",
    demo: {
      // Compaction is deliberately held off until the next chapter: this one
      // is about versions accumulating, and a background merge tidying them
      // away mid-demo would contradict the text on screen.
      config: { l0Trigger: 20, numLevels: 2 },
      panels: ["memtable", "levels", "metrics"],
      expandTables: true,
      buttons: [
        {
          label: "put colour=red",
          run: async (db, d) => {
            await db.put("colour", "red");
            d.say("version 1 written");
          },
        },
        {
          label: "update to blue",
          run: async (db, d) => {
            await db.put("colour", "blue");
            await db.flushNow();
            await db.quiesce();
            d.say("version 2 written — version 1 is still on disk, just shadowed");
          },
        },
        {
          label: "update to green",
          run: async (db, d) => {
            await db.put("colour", "green");
            await db.flushNow();
            await db.quiesce();
            d.say("version 3 — three copies of one key now exist");
          },
        },
        {
          label: "delete colour",
          run: async (db, d) => {
            await db.delete("colour");
            await db.flushNow();
            await db.quiesce();
            const v = await db.get("colour");
            d.say(
              `deleted → get returns ${v === null ? "not found" : v} — and a fourth record was added to say so`,
            );
          },
        },
      ],
      note: "A ✗ marks a tombstone. Notice the data on disk only grows.",
    },
    after: [
      "Left alone, this is untenable: obsolete versions and tombstones would accumulate until they dwarfed the live data.",
    ],
    cliffhanger:
      "Something has to go back and clean up. But it has to do so without ever blocking a write, and without breaking the newest-wins rule reads depend on.",
  },

  // ---------------------------------------------------------------- 7
  {
    id: "compaction",
    nav: "Compaction",
    kicker: "Consequence 7",
    title: "Compaction: the background merge",
    body: [
      "Compaction takes several files, merges them, keeps only the newest version of each key, and writes the result out as new files. Then it swaps them in and deletes the originals.",
      "Because every input file is already sorted, this is a merge, not a sort — walk all the files in parallel, always taking the smallest key next. One linear pass, no matter how large the files are. This is the third time sortedness has paid off.",
      "Tombstones get a special rule, and it is the subtlest part of the whole design. A tombstone can only be thrown away when it reaches the bottom level. Discard one earlier and an older copy of that key, sitting in a deeper file, would suddenly become the newest surviving version — and deleted data would come back to life.",
    ],
    tryIt:
      "Build up some garbage, then compact. Watch the file count drop — but notice the tombstone survives. Then push the data all the way to the bottom level and watch it finally go.",
    demo: {
      panels: ["levels", "metrics", "trace"],
      expandTables: true,
      buttons: [
        {
          label: "make a mess",
          run: async (db, d) => {
            for (const [k, v] of [
              ["apple", "red"],
              ["apple", "green"],
              ["fig", "purple"],
              ["apple", "gold"],
              ["fig", "black"],
            ] as [string, string][]) {
              await db.put(k, v);
              await db.flushNow();
            }
            await db.delete("fig");
            await db.flushNow();
            d.say("several files, mostly obsolete versions and one tombstone");
          },
        },
        {
          label: "compact",
          run: async (db, d) => {
            d.say("merging…");
            db.maybeCompact();
            await db.quiesce();
            d.say(
              "merged — obsolete versions are gone, but the tombstone is still there: " +
                "this merge didn't write the bottom level, so an older 'fig' could still be hiding below it",
            );
          },
        },
        {
          label: "compact to the bottom",
          title: "Shrink the level budget so the data is pushed to the last level",
          run: async (db, d) => {
            const before = db.metrics.droppedTombstones;
            // Squeezing the level budget forces the merge to continue down to
            // the last level — the same thing more data would eventually do.
            db.setConfig({ levelBaseBytes: 1 });
            await db.quiesce();
            const dropped = db.metrics.droppedTombstones - before;
            d.say(
              dropped > 0
                ? `reached the bottom level — ${dropped} tombstone(s) discarded, because nothing older can survive beneath them`
                : "already at the bottom",
            );
          },
        },
      ],
      note: "The merge is a real k-way merge over the serialized files, not a rebuild from memory.",
    },
    after: [
      "Compaction is where the deferred cost finally gets paid. Writes were cheap because we postponed the work; this is the bill, and it arrives in the background rather than in the write path.",
    ],
    cliffhanger:
      "One question remains: which files should be merged together, and when? That choice turns out to define the engine's entire performance character.",
  },

  // ---------------------------------------------------------------- 8
  {
    id: "levels",
    nav: "Levels",
    kicker: "Consequence 8",
    title: "Organising files into levels",
    body: [
      "Files are arranged into levels. Freshly flushed memtables land in L0, and each compaction pushes data one level deeper. Levels get larger as they get deeper, typically by a factor of 10 in production systems.",
      "L0 is a special case. Its files come straight from memtables, each covering whatever keys happened to be written during that window, so their key ranges overlap. A lookup may have to check several of them.",
      "From L1 down, compaction maintains a much stronger property: within a level, files never overlap. Each key range belongs to exactly one file. That means a lookup checks at most one file per level — which is what keeps reads scalable as the database grows.",
      "That guarantee is why a compaction pulls in the files it overlaps in the target level: it must rewrite them to preserve the invariant.",
    ],
    tryIt:
      "Write enough to trigger several rounds and watch data descend, with L1 and below staying tidy.",
    demo: {
      config: { memtableBytes: 100, l0Trigger: 2, numLevels: 3 },
      panels: ["levels", "metrics"],
      expandTables: true,
      buttons: [
        {
          label: "write a batch",
          run: async (db, d) => {
            const keys = "abcdefghijklmnop".split("");
            for (const k of keys.slice(0, 8)) {
              await db.put("key" + k, "val" + k);
            }
            await db.flushNow();
            await db.quiesce();
            d.say("flushed and compacted — check the key ranges in L1");
          },
        },
        {
          label: "write more",
          run: async (db, d) => {
            const keys = "qrstuvwxyz".split("");
            for (const k of keys) await db.put("key" + k, "val" + k);
            await db.flushNow();
            await db.quiesce();
            d.say("deeper levels stay sorted and non-overlapping");
          },
        },
      ],
      note: "Compare the key ranges of files within one level: L0 may overlap, L1+ must not.",
    },
    after: [
      "This engine verifies that invariant programmatically — after heavy random workloads it asserts that every L1+ level is sorted with no overlapping ranges.",
    ],
    cliffhanger:
      "Now the real engineering question: how aggressively should you compact? Merge constantly and reads stay fast but you rewrite data endlessly. Merge lazily and writes get cheap but reads suffer.",
  },

  // ---------------------------------------------------------------- 9
  {
    id: "amplification",
    nav: "The trade-off",
    kicker: "The payoff",
    title: "Three costs, and you cannot minimise all of them",
    body: [
      "Storage engineers measure this design with three numbers, and every tuning decision trades them against each other.",
      "Write amplification — how many bytes hit the disk per byte you actually stored. Above 1 because compaction rewrites the same data repeatedly as it descends.",
      "Read amplification — how many files a lookup has to consult. Bloom filters make each check cheap, but they don't make it free.",
      "Space amplification — how many bytes sit on disk per byte of live data. Obsolete versions and tombstones take up room until compaction reclaims them.",
      "The two classic strategies sit at opposite ends. Leveled compaction (RocksDB's default) merges eagerly to keep one file per level: reads and space stay tight, writes get rewritten a lot. Size-tiered (Cassandra's) waits until several similar-sized files pile up and merges them at once: cheap writes, but more files to check and more duplicates lingering.",
    ],
    tryIt:
      "Run an identical workload under both strategies and compare. Same data, same seed — only the compaction policy differs.",
    demo: {
      panels: ["metrics", "levels"],
      buttons: [
        {
          label: "run workload · leveled",
          run: async (_db, d) => {
            await d.runStrategy("leveled");
          },
        },
        {
          label: "run workload · size-tiered",
          run: async (_db, d) => {
            await d.runStrategy("tiered");
          },
        },
      ],
      note: "Identical operations both times. Any difference in the numbers comes purely from the merge policy.",
    },
    after: [
      "There is no universally correct setting — it depends on whether your workload is write-heavy, read-heavy, or space-constrained. That is precisely why real engines expose these as knobs, and why the sandbox lets you turn them under load.",
    ],
  },

  // ---------------------------------------------------------------- 10
  {
    id: "sandbox",
    nav: "Your turn",
    kicker: "Done",
    title: "You now know how an LSM tree works",
    body: [
      "Every piece existed for a reason, and each reason came from the previous piece:",
      "Random writes are slow, so buffer in memory. Memory dies, so log first. Buffers fill, so flush sorted files. Files pile up, so search newest-first. Searching many files is slow, so add bloom filters. Files are immutable, so updates leave old versions and deletes write tombstones. Garbage accumulates, so compact in the background. Tombstones can't drop early or deleted data returns. Compaction strategy decides your write, read, and space trade-off.",
      "That is the whole design. What is left is scale and engineering detail — concurrency, block caches, per-level bloom tuning, and compaction scheduling that avoids latency spikes.",
      "The sandbox is the same engine without the training wheels: real workload generators, live amplification metrics, adjustable knobs, and persistence to IndexedDB so refreshing the page becomes a genuine crash-recovery test.",
    ],
    after: [
      "If you want to go deeper, the sandbox's 'How it works' panel has a component-by-component reference and an annotated bibliography — starting with O'Neil et al.'s 1996 paper that introduced this structure, and Luo & Carey's 2020 survey of everything that happened since.",
    ],
  },
];
